import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import OrdersByDayChart from './Charts/OrdersByDayChart';
import OrdersByTableChart from './Charts/OrdersByTableChart';
import OrdersByServerChart from './Charts/OrdersByServerChart';
import TicketViewer from './Charts/TicketViewer';
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { apiFetch } from '../../../../utils/api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toYmdDayNumber = (ymd) => {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
};

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const s = String(isoString).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return s;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const hour24 = Number(m[4]);
  const minute = Number(m[5]);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return s;
  if (!Number.isFinite(hour24) || !Number.isFinite(minute)) return s;
  if (month < 1 || month > 12) return s;

  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = (hour24 % 12) || 12;
  return `${MONTHS[month - 1]} ${day}, ${year}, ${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ampm}`;
};

const formatYmdDate = (ymd) => {
  if (!ymd) return '-';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return '-';
  if (m < 1 || m > 12) return '-';
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};

const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
    <div className="bg-white dark:bg-gray-900 p-6 rounded shadow-lg border border-gray-200 dark:border-gray-700 max-w-3xl w-full relative">
      <button
        className="absolute top-2 right-2 text-gray-600 dark:text-gray-300 hover:text-red-500"
        onClick={onClose}
      >
        <Icon icon="fluent-emoji:multiply" className="h-5 w-5" aria-hidden="true" />
      </button>
      {children}
    </div>
  </div>
);

const OrdersChartsSection = ({
  isDark = false,
  filters = {},
  ordersByDay = [],
  ordersByTable = [],
  ordersByServer = [],
  ordersSummary = { total_orders: 0 },
  ordersList = [],
  ordersItemsCount = [],
  ordersBumpMinutes = []
}) => {
  const [search, setSearch] = useState('');
  const [ticketDetail, setTicketDetail] = useState(null);

  // Build item count map
  const itemCountsMap = {};
  (Array.isArray(ordersItemsCount) ? ordersItemsCount : []).forEach(({ order_id, item_count, bumped_at }) => {
    itemCountsMap[order_id] = { item_count, bumped_at };
  });

  // Filter for search box
  const filteredOrders = (ordersList || []).filter((order) => {
    const term = search.trim().toLowerCase();
    return (
      order.order_id?.toString().includes(term) ||
      (order.member_name && order.member_name.toLowerCase().includes(term)) ||
      (order.server_name && order.server_name.toLowerCase().includes(term)) ||
      (order.table_name && order.table_name.toLowerCase().includes(term))
    );
  });

  // Build enriched rows before DataGrid
  const rows = filteredOrders.map((order) => ({
    ...order,
    received_atFormatted: formatDateTime(order.received_at),
    items: itemCountsMap[order.order_id]?.item_count ?? 0,
  }));

  // DataGrid columns
  const columns = [
    { field: 'order_id', headerName: 'Order ID', width: 120 },
    { field: 'received_atFormatted', headerName: 'Received At', flex: 1 },
    { field: 'table_name', headerName: 'Table', flex: 1 },
    { field: 'server_name', headerName: 'Server', flex: 1 },
    { field: 'member_name', headerName: 'Member', flex: 1 },
    { field: 'items', headerName: 'Items', width: 100 },
    {
      field: 'ticket',
      headerName: 'Ticket',
      width: 130,
      renderCell: (params) => (
        <span
          className="text-blue-600 dark:text-blue-400 underline cursor-pointer"
          onClick={async () => {
            const order = params.row;
            try {
              const detailRes = await apiFetch(
                `/updates-reports/orders-ticket-detail/${order.order_id}`
              );
              const detail = await detailRes.json();
              setTicketDetail({
                ...detail,
                order_meta: {
                  order_id: order.order_id,
                  table_name: order.table_name,
                  server_name: order.server_name,
                  member_name: order.member_name,
                  received_at: order.received_at,
                },
              });
            } catch (err) {
              console.error('Error loading ticket detail:', err);
            }
          }}
        >
          View Ticket
        </span>
      ),
    },
  ];

  // Derived stats
  const totalItems = Object.values(itemCountsMap).reduce(
    (sum, entry) => sum + (Number(entry.item_count) || 0),
    0
  );

  // Force it to be a number, even if the API sends a string
  const totalOrders = Number(ordersSummary?.total_orders || 0);

  const avgItemsPerOrder =
    totalOrders > 0
      ? (totalItems / totalOrders).toFixed(1)
      : '-';

  const bumpValues = (Array.isArray(ordersBumpMinutes) ? ordersBumpMinutes : [])
    .map((r) => Number(r?.bump_minutes))
    .filter((n) => Number.isFinite(n) && n >= 0);
  const avgTicketBumpTime =
    bumpValues.length > 0
      ? (bumpValues.reduce((sum, n) => sum + n, 0) / bumpValues.length).toFixed(2)
      : '-';
  const medianTicketBumpTime = (() => {
    if (!bumpValues.length) return '-';
    const sorted = [...bumpValues].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 1) return sorted[mid].toFixed(2);
    return ((sorted[mid - 1] + sorted[mid]) / 2).toFixed(2);
  })();

  const topOrdersDay = (() => {
    if (!Array.isArray(ordersByDay) || !ordersByDay.length) return null;

    const inSelectedRange = (row) => {
      const day = String(row?.date || '').slice(0, 10);
      if (!day) return false;
      if (filters?.startDate && day < String(filters.startDate)) return false;
      if (filters?.endDate && day > String(filters.endDate)) return false;
      return true;
    };

    const scopedDays =
      filters?.startDate || filters?.endDate
        ? ordersByDay.filter(inSelectedRange)
        : ordersByDay;

    if (!scopedDays.length) return null;

    return scopedDays.reduce((best, cur) =>
      Number(cur?.total_orders || 0) > Number(best?.total_orders || 0) ? cur : best
    );
  })();
  const topOrdersDayLabel = formatYmdDate(topOrdersDay?.date);
  const topOrdersDayCount = Number(topOrdersDay?.total_orders || 0);

  const avgOrdersPerDay = (() => {
    const total = Number(ordersSummary?.total_orders || 0);
    if (!total) return '-';

    const todayYmd = new Date().toISOString().slice(0, 10);
    const minDateStr = Array.isArray(ordersByDay) && ordersByDay.length
      ? ordersByDay.map((d) => String(d?.date || '').slice(0, 10)).filter(Boolean).sort()[0]
      : null;
    const startYmd = filters?.startDate || minDateStr || null;
    const endYmd = filters?.endDate || todayYmd;
    const startDay = toYmdDayNumber(startYmd);
    const endDay = toYmdDayNumber(endYmd);
    if (!Number.isFinite(startDay) || !Number.isFinite(endDay)) return '-';
    const days = Math.max(1, (endDay - startDay) + 1);
    return (total / days).toFixed(2);
  })();

  return (
    <div className="p-4 dark:text-gray-100">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <div className="bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-blue-700 dark:text-blue-300 font-medium">Total Orders</h4>
          <p className="text-2xl text-blue-900 dark:text-blue-200 font-bold mt-1">
            {ordersSummary.total_orders?.toLocaleString() ?? '-'}
          </p>
        </div>

        <div className="bg-purple-50 border border-purple-200 dark:bg-purple-900/20 dark:border-purple-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-purple-700 dark:text-purple-300 font-medium">Avg. Items per Order</h4>
          <p className="text-2xl text-purple-900 dark:text-purple-200 font-bold mt-1">{avgItemsPerOrder}</p>
        </div>

        <div className="bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-green-700 dark:text-green-300 font-medium">Top Server</h4>
          {ordersByServer.length > 0 ? (
            <>
              <p className="text-md text-green-900 dark:text-green-200 font-semibold truncate">
                {ordersByServer[0].server_name || '-'}
              </p>
              <p className="text-lg text-green-900 dark:text-green-200 font-bold">
                {ordersByServer[0].total_orders.toLocaleString()}
              </p>
            </>
          ) : (
            <p className="text-md text-green-900 dark:text-green-200 font-semibold">-</p>
          )}
        </div>

        <div className="bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-yellow-700 dark:text-yellow-300 font-medium">Top Table</h4>
          {ordersByTable.length > 0 ? (
            <>
              <p className="text-md text-yellow-900 dark:text-yellow-200 font-semibold truncate">
                {ordersByTable[0].table_name || '-'}
              </p>
              <p className="text-lg text-yellow-900 dark:text-yellow-200 font-bold">
                {ordersByTable[0].total_orders.toLocaleString()}
              </p>
            </>
          ) : (
            <p className="text-md text-yellow-900 dark:text-yellow-200 font-semibold">-</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <div id="report-top-orders-day" className="bg-indigo-50 border border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-indigo-700 dark:text-indigo-300 font-medium">Top Orders Day</h4>
          <p className="text-md text-indigo-900 dark:text-indigo-200 font-semibold mt-1">{topOrdersDayLabel}</p>
          <p className="text-xl text-indigo-900 dark:text-indigo-200 font-bold">{topOrdersDayCount ? topOrdersDayCount.toLocaleString() : '-'}</p>
        </div>

        <div id="report-avg-orders-per-day" className="bg-cyan-50 border border-cyan-200 dark:bg-cyan-900/20 dark:border-cyan-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-cyan-700 dark:text-cyan-300 font-medium">Avg Orders / Day</h4>
          <p className="text-2xl text-cyan-900 dark:text-cyan-200 font-bold mt-1">{avgOrdersPerDay}</p>
        </div>

        <div id="report-avg-ticket-bump-time" className="bg-rose-50 border border-rose-200 dark:bg-rose-900/20 dark:border-rose-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-rose-700 dark:text-rose-300 font-medium">Avg Ticket Bump (mins)</h4>
          <p className="text-2xl text-rose-900 dark:text-rose-200 font-bold mt-1">{avgTicketBumpTime}</p>
        </div>

        <div id="report-median-ticket-bump-time" className="bg-emerald-50 border border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800 rounded-lg p-4 shadow-sm">
          <h4 className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">Median Ticket Bump (mins)</h4>
          <p className="text-2xl text-emerald-900 dark:text-emerald-200 font-bold mt-1">{medianTicketBumpTime}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="mb-10 space-y-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 rounded shadow">
          <h3 className="text-blue-700 dark:text-blue-300 font-semibold mb-2">Orders by Day</h3>
          <OrdersByDayChart data={ordersByDay} />
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 rounded shadow">
          <h3 className="text-blue-700 dark:text-blue-300 font-semibold mb-2">Orders by Server</h3>
          <OrdersByServerChart data={ordersByServer} />
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 rounded shadow">
          <h3 className="text-blue-700 dark:text-blue-300 font-semibold mb-2">Orders by Table</h3>
          <OrdersByTableChart data={ordersByTable} />
        </div>
      </div>

      {/* Orders Summary */}
      <div className="my-6">
        <h2 className="text-xl font-semibold text-blue-800 dark:text-blue-300 mb-2">Orders Summary</h2>
        <p className="text-lg">
          Total Tickets: <strong>{ordersSummary?.total_orders ?? 0}</strong>
        </p>
      </div>

      {/* DataGrid */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 mt-10 p-4 rounded shadow">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
          <h3 className="text-blue-700 dark:text-blue-300 font-semibold text-lg">All Orders</h3>
          <input
            type="text"
            placeholder="Search by Order ID, Server, Table..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border px-3 py-1 rounded shadow-sm w-full sm:w-64 bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
          />
        </div>

        <div style={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={50}
            rowsPerPageOptions={[25, 50, 100]}
            getRowId={(row) => row.order_id}
            disableSelectionOnClick
            slots={{
              toolbar: GridToolbar, // Columns, Filters, Density, Export
            }}
            sx={{
              backgroundColor: isDark ? "#1f2937" : "#ffffff",
              color: isDark ? "#e5e7eb" : "#111827",
              borderColor: isDark ? "#374151" : "#e5e7eb",
              "& .MuiDataGrid-main": {
                backgroundColor: isDark ? "#1f2937" : "#ffffff",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: isDark ? "#374151" : "#f4f4f4",
                fontWeight: "bold",
                color: isDark ? "#f3f4f6" : "#111827",
                borderBottomColor: isDark ? "#4b5563" : "#e5e7eb",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: isDark ? "#374151" : "#f4f4f4",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: isDark ? "#f9fafb" : "#111827",
                fontWeight: 700,
              },
              "& .MuiDataGrid-iconSeparator": {
                color: isDark ? "#6b7280" : "#d1d5db",
              },
              "& .MuiDataGrid-cell": {
                borderBottomColor: isDark ? "#374151" : "#e5e7eb",
                color: isDark ? "#e5e7eb" : "#111827",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: isDark ? "#111827" : "#f9fafb",
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: isDark ? "#334155" : "#DBEAFE",
              },
              "& .MuiDataGrid-row.Mui-selected:hover": {
                backgroundColor: isDark ? "#3b4a62" : "#BFDBFE",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTopColor: isDark ? "#374151" : "#e5e7eb",
                backgroundColor: isDark ? "#1f2937" : "#ffffff",
              },
              "& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiSvgIcon-root, & .MuiDataGrid-toolbarContainer button": {
                color: isDark ? "#d1d5db" : "#374151",
              },
            }}
          />
        </div>
      </div>

      {/* Ticket Modal */}
      {ticketDetail && (
        <Modal onClose={() => setTicketDetail(null)}>
          <div className="space-y-4 max-h-[80vh] overflow-y-auto">
            <h4 className="text-blue-700 dark:text-blue-300 font-semibold">
              Ticket Detail: Order #{ticketDetail?.order_id || ticketDetail?.order_meta?.order_id}
            </h4>
            <TicketViewer ticketDetail={ticketDetail} />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OrdersChartsSection;
